import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { createEnquiry } from "../lib/leads";

const REQ_OPTIONS = [
  "Premium Green Cardamom",
  "6mm+ Cardamom",
  "7mm+ Cardamom",
  "8mm+ Cardamom",
  "Jumbo Grade",
  "Dry Fruits",
  "Mixed Wholesale Order",
];

const initial = {
  name: "",
  mobile: "",
  business_name: "",
  location: "",
  requirement: REQ_OPTIONS[0],
  quantity: "",
};

export const EnquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Enter your name";
    const mob = form.mobile.replace(/\s|-/g, "");
    if (!/^[6-9]\d{9}$|^\+?\d{10,15}$/.test(mob))
      e.mobile = "Enter a valid mobile number";
    if (!form.business_name || form.business_name.trim().length < 2)
      e.business_name = "Enter your business name";
    if (!form.location || form.location.trim().length < 2)
      e.location = "Enter your city / area";
    if (!form.requirement) e.requirement = "Select a requirement";
    if (!form.quantity || form.quantity.trim().length < 1)
      e.quantity = "Enter quantity needed";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) =>
    setForm((f) => ({ ...f, [k]: ev.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await createEnquiry(form);
      setSuccess(true);
      toast.success("Enquiry received. Our trade desk will reach out within an hour.");
      setForm(initial);
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) {
      toast.error(
        err?.message ||
          "Could not submit. Please try WhatsApp or call us."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="section-pad bg-[#0E4D3D] text-white relative overflow-hidden"
    >
      {/* Decorative */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 80% 20%, #C9A227 0, transparent 35%)",
        }}
      />

      <div className="container-luxury relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="heading-eyebrow">
                <span className="line" />
                <span className="text">Wholesale Enquiry</span>
              </div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight">
                Request your
                <br />
                <span className="italic font-light text-[#C9A227]">
                  best wholesale rate.
                </span>
              </h2>
              <p className="mt-6 text-white/80 font-light leading-relaxed">
                Share your requirement — our trade desk replies within one
                business hour with a live grade-wise quote and sample dispatch
                options.
              </p>

              <div className="mt-10 space-y-5">
                {[
                  ["Response time", "Within 60 minutes"],
                  ["Samples available", "Across all 4 grades"],
                  ["Pricing valid", "7 working days"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-baseline justify-between border-b border-white/15 pb-3"
                  >
                    <span className="label-overline text-white/60">{k}</span>
                    <span className="font-serif text-[#C9A227] text-lg">
                      {v}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              onSubmit={submit}
              data-testid="enquiry-form"
              className="bg-[#FAF8F2] text-[#2A2A2A] p-7 md:p-12"
            >
              {success ? (
                <div className="py-16 text-center" data-testid="enquiry-success">
                  <CheckCircle2 className="w-14 h-14 text-[#0E4D3D] mx-auto" strokeWidth={1.3} />
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl text-[#0E4D3D]">
                    Enquiry received.
                  </h3>
                  <p className="mt-3 text-[#4A4A4A] font-light max-w-md mx-auto">
                    Our trade desk will reach out within the hour with a
                    grade-wise quote. Meanwhile, feel free to WhatsApp us.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="label-overline text-[#0E4D3D]">Your Name</label>
                      <input
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Full name"
                        className="input-luxury mt-2"
                        data-testid="enquiry-name"
                      />
                      {errors.name && (
                        <p className="text-xs text-red-700 mt-1" data-testid="enquiry-name-error">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-overline text-[#0E4D3D]">Mobile Number</label>
                      <input
                        value={form.mobile}
                        onChange={onChange("mobile")}
                        placeholder="+91 98xxx xxxxx"
                        className="input-luxury mt-2"
                        data-testid="enquiry-mobile"
                      />
                      {errors.mobile && (
                        <p className="text-xs text-red-700 mt-1" data-testid="enquiry-mobile-error">{errors.mobile}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-overline text-[#0E4D3D]">Business Name</label>
                      <input
                        value={form.business_name}
                        onChange={onChange("business_name")}
                        placeholder="Company / shop name"
                        className="input-luxury mt-2"
                        data-testid="enquiry-business"
                      />
                      {errors.business_name && (
                        <p className="text-xs text-red-700 mt-1">{errors.business_name}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-overline text-[#0E4D3D]">Location</label>
                      <input
                        value={form.location}
                        onChange={onChange("location")}
                        placeholder="City / area"
                        className="input-luxury mt-2"
                        data-testid="enquiry-location"
                      />
                      {errors.location && (
                        <p className="text-xs text-red-700 mt-1">{errors.location}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-overline text-[#0E4D3D]">Requirement</label>
                      <select
                        value={form.requirement}
                        onChange={onChange("requirement")}
                        className="input-luxury mt-2 bg-transparent"
                        data-testid="enquiry-requirement"
                      >
                        {REQ_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label-overline text-[#0E4D3D]">Quantity Needed</label>
                      <input
                        value={form.quantity}
                        onChange={onChange("quantity")}
                        placeholder="e.g. 100 kg / monthly"
                        className="input-luxury mt-2"
                        data-testid="enquiry-quantity"
                      />
                      {errors.quantity && (
                        <p className="text-xs text-red-700 mt-1">{errors.quantity}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-between">
                    <p className="text-xs text-[#4A4A4A] font-light max-w-sm leading-relaxed">
                      By submitting, you agree to be contacted by our trade desk
                      regarding wholesale enquiries. No spam.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="enquiry-submit"
                      className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? "Sending..." : "Send Enquiry"}
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
};
